import { ArrowRight, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";

import AuthLayout from "./AuthLayout";
import FormField from "@/components/common/FormField";
import { Button } from "@/components/ui/button";
import { errorTypes } from "@/utility/validation";
import { RoutesLink } from "@/utility/RouteLinks";
import { AuthService } from "@/services/Auth/Auth";
import { ThrowError } from "@/api/callApi";
import { setCookies, showToastNotification } from "@/utility/helper";
import type { ILoginData } from "@/interface/auth.model";

type Inputs = {
  username: string;
  password: string;
};

const Login: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    mode: "onBlur",
    defaultValues: {
      username: "admin",
      password: "123456",
    },
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = async (data: Inputs) => {
    setIsLoading(true);
    try {
      const business = import.meta.env.VITE_BUSINESS;
      const deviceIdKey = business + "deviceId";
      let deviceId = localStorage.getItem(deviceIdKey);
      if (!deviceId) {
        deviceId = "2025_" + uuidv4();
        localStorage.setItem(deviceIdKey, deviceId);
      }

      const payload: ILoginData = {
        username: data.username,
        password: data.password,
        deviceId,
        deviceToken: "firebase-token",
      };

      if (searchParams.get("client_id")) {
        payload.client_id = searchParams.get("client_id") ?? undefined;
        payload.redirect_uri = searchParams.get("redirect_uri") ?? undefined;
        payload.scope = searchParams.get("scope") ?? undefined;
        payload.state = searchParams.get("state") ?? undefined;
        payload.code_challenge =
          searchParams.get("code_challenge") ?? undefined;
        payload.code_challenge_method =
          searchParams.get("code_challenge_method") ?? undefined;
        payload.response_type = searchParams.get("response_type") ?? undefined;
      }

      const response = await AuthService.login(payload);

      if (!response?.success) {
        showToastNotification("danger", response?.message || "Login failed");
        return;
      }

      const { accessToken, refreshToken } = response.data;
      setCookies(business + "token", accessToken);
      setCookies(business + "refreshToken", refreshToken);
      localStorage.setItem(business + "token", accessToken);

      showToastNotification("success", response?.message || "Login successful");

      navigate(RoutesLink.dashboard);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="mb-8">
        <p
          className="text-[11px] uppercase tracking-[0.16em] font-medium mb-2"
          style={{ color: "#2C4A6E", fontFamily: "'IBM Plex Mono', monospace" }}
        >
          Admin access
        </p>
        <h2
          className="text-[1.75rem] font-semibold text-[#1E1B16] mb-1.5 leading-tight"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Sign in
        </h2>
        <p className="text-[#85806F] text-[13.5px]">
          Enter your credentials to open today's register.
        </p>
      </div>

      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-5">
          <FormField
            label="Username"
            placeholder="admin"
            type="text"
            {...register("username", {
              ...errorTypes.required("Username"),
            })}
            error={errors.username?.message}
          />
          <FormField
            type="password"
            label="Password"
            placeholder="••••••••"
            {...register("password", {
              ...errorTypes.required("Password"),
              ...errorTypes.password,
            })}
            error={errors.password?.message}
          />
          <div className="flex justify-end">
            <Link
              to={RoutesLink.authRoutes.forgot}
              className="text-sm font-medium text-[#2C4A6E] hover:text-[#1E3552] transition-colors"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        <Button
          type="submit"
          className="w-full cursor-pointer"
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 strokeWidth={2} className="w-4 h-4 animate-spin" />
          ) : (
            "Sign In"
          )}
          {!isLoading && (
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
          )}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default Login;